import { BOOLEAN, Host, effect, isString, setAttribute, useState } from "./vidstack-D9mvYVIz.js";
import { useMediaContext } from "./vidstack-CYE89tBD.js";
import { cloneTemplate, cloneTemplateContent, createTemplate, requestScopedAnimationFrame } from "./vidstack-CgfrU1hS.js";
import { AirPlayButton, AudioRadioGroup, CaptionButton, CaptionsRadioGroup, FullscreenButton, LiveButton, Menu, MenuButton, MenuItem, MenuItems, MuteButton, PIPButton, PlayButton, QualityRadioGroup, Radio, SeekButton, Slider, SliderPreview, SliderValue, SpeedRadioGroup, Thumbnail, Time, TimeSlider, VolumeSlider } from "./vidstack-D0Mmfcu7.js";
//#region src/elements/define/thumbnail-element.ts
const imgTemplate = /* @__PURE__ */ createTemplate("<img loading=\"eager\" decoding=\"async\" aria-hidden=\"true\">");
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/thumbnail}
* @example
* ```html
* <media-player>
*   <!-- ... -->
*   <media-thumbnail
*     src="https://files.vidstack.io/thumbnails.vtt"
*     time="10"
*   ></media-thumbnail>
* </media-player>
* ```
*/
var MediaThumbnailElement = class extends Host(HTMLElement, Thumbnail) {
	static tagName = "media-thumbnail";
	static attrs = { crossOrigin: "crossorigin" };
	#media;
	#img = this.#createImg();
	onSetup() {
		this.#media = useMediaContext();
		this.$state.img.set(this.#img);
	}
	onConnect() {
		const { src, crossOrigin } = this.$state;
		if (this.#img.parentNode !== this) this.prepend(this.#img);
		effect(() => {
			setAttribute(this.#img, "src", src());
			setAttribute(this.#img, "crossorigin", crossOrigin());
		});
	}
	#createImg() {
		return cloneTemplateContent(imgTemplate);
	}
};
//#endregion
//#region src/elements/define/time-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/time}
* @example
* ```html
* <media-time type="current"></media-time>
* ```
* @example
* ```html
* <!-- Remaining time. -->
* <media-time type="current" remainder></media-time>
* ```
*/
var MediaTimeElement = class extends Host(HTMLElement, Time) {
	static tagName = "media-time";
	onConnect() {
		effect(() => {
			this.textContent = this.$state.timeText();
		});
	}
};
//#endregion
//#region src/elements/define/buttons/airplay-button-element.ts
/**
* @example
* ```html
* <media-airplay-button>
*   <media-icon type="airplay"></media-icon>
* </media-airplay-button>
* ```
*/
var MediaAirPlayButtonElement = class extends Host(HTMLElement, AirPlayButton) {
	static tagName = "media-airplay-button";
};
//#endregion
//#region src/elements/define/buttons/caption-button-element.ts
/**
* @example
* ```html
* <media-caption-button>
*   <media-icon type="closed-captions-on"></media-icon>
*   <media-icon type="closed-captions"></media-icon>
* </media-caption-button>
* ```
*/
var MediaCaptionButtonElement = class extends Host(HTMLElement, CaptionButton) {
	static tagName = "media-caption-button";
};
//#endregion
//#region src/elements/define/buttons/fullscreen-button-element.ts
/**
* @example
* ```html
* <media-fullscreen-button>
*   <media-icon type="fullscreen"></media-icon>
*   <media-icon type="fullscreen-exit"></media-icon>
* </media-fullscreen-button>
* ```
*/
var MediaFullscreenButtonElement = class extends Host(HTMLElement, FullscreenButton) {
	static tagName = "media-fullscreen-button";
};
//#endregion
//#region src/elements/define/buttons/live-button-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/buttons/live-button}
* @example
* ```html
* <media-live-button>
*   <!-- ... -->
* </media-live-button>
* ```
*/
var MediaLiveButtonElement = class extends Host(HTMLElement, LiveButton) {
	static tagName = "media-live-button";
};
//#endregion
//#region src/elements/define/buttons/mute-button-element.ts
/**
* @example
* ```html
* <media-mute-button>
*   <media-icon type="mute"></media-icon>
*   <media-icon type="volume-low"></media-icon>
*   <media-icon type="volume-high"></media-icon>
* </media-mute-button>
* ```
*/
var MediaMuteButtonElement = class extends Host(HTMLElement, MuteButton) {
	static tagName = "media-mute-button";
};
//#endregion
//#region src/elements/define/buttons/pip-button-element.ts
/**
* @example
* ```html
* <media-pip-button>
*   <media-icon type="picture-in-picture"></media-icon>
*   <media-icon type="picture-in-picture-exit"></media-icon>
* </media-pip-button>
* ```
*/
var MediaPIPButtonElement = class extends Host(HTMLElement, PIPButton) {
	static tagName = "media-pip-button";
};
//#endregion
//#region src/elements/define/buttons/play-button-element.ts
/**
* @example
* ```html
* <media-play-button>
*   <media-icon type="play"></media-icon>
*   <media-icon type="pause"></media-icon>
*   <media-icon type="replay"></media-icon>
* </media-play-button>
* ```
*/
var MediaPlayButtonElement = class extends Host(HTMLElement, PlayButton) {
	static tagName = "media-play-button";
};
//#endregion
//#region src/elements/define/buttons/seek-button-element.ts
/**
* @example
* ```html
* <!-- Forward +30s on each press. -->
* <media-seek-button seconds="+30">
*   <media-icon type="seek-forward"></media-icon>
* </media-seek-button>
* <!-- Backward -30s on each press. -->
* <media-seek-button seconds="-30">
*   <media-icon type="seek-backward"></media-icon>
* </media-seek-button>
* ```
*/
var MediaSeekButtonElement = class extends Host(HTMLElement, SeekButton) {
	static tagName = "media-seek-button";
};
//#endregion
//#region src/elements/define/menus/_template.ts
function renderMenuItemsTemplate(el, onCreate) {
	requestScopedAnimationFrame(() => {
		if (!el.connectScope) return;
		const template = el.querySelector("template");
		if (!template) return;
		effect(() => {
			const options = el.getOptions();
			cloneTemplate(template, options.length, (radio, i) => {
				const { label, value } = options[i], labelEl = radio.querySelector(`[data-part="label"]`);
				radio.setAttribute("value", value);
				if (labelEl) if (isString(label)) labelEl.textContent = label;
				else effect(() => {
					labelEl.textContent = label();
				});
				onCreate?.(radio, options[i], i);
			});
		});
	});
}
//#endregion
//#region src/elements/define/menus/audio-radio-group-element.ts
/**
* @part label - Contains the audio track option label.
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/audio-radio-group}
* @example
* ```html
* <media-menu>
*   <!-- ... -->
*   <media-menu-items>
*     <media-audio-radio-group>
*       <template>
*         <media-radio>
*           <span data-part="label"></span>
*         </media-radio>
*       </template>
*     </media-audio-radio-group>
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaAudioRadioGroupElement = class extends Host(HTMLElement, AudioRadioGroup) {
	static tagName = "media-audio-radio-group";
	onConnect() {
		renderMenuItemsTemplate(this);
	}
};
//#endregion
//#region src/elements/define/menus/captions-radio-group-element.ts
/**
* @part label - Contains the caption/subtitle option label.
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/captions-radio-group}
* @example
* ```html
* <media-menu>
*   <!-- ... -->
*   <media-menu-items>
*     <media-captions-radio-group>
*       <template>
*         <media-radio>
*           <span data-part="label"></span>
*         </media-radio>
*       </template>
*     </media-captions-radio-group>
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaCaptionsRadioGroupElement = class extends Host(HTMLElement, CaptionsRadioGroup) {
	static tagName = "media-captions-radio-group";
	onConnect() {
		renderMenuItemsTemplate(this);
	}
};
//#endregion
//#region src/elements/define/menus/menu-element.ts
/**
* @part close-target - Closes menu when pressed.
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/menu}
* @example
* ```html
* <media-menu>
*   <media-menu-button aria-label="Settings">
*     <media-icon type="settings"></media-icon>
*   </media-menu-button>
*   <media-menu-items>
*     <!-- ... -->
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaMenuElement = class extends Host(HTMLElement, Menu) {
	static tagName = "media-menu";
};
//#endregion
//#region src/elements/define/menus/menu-button-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/menu}
* @example
* ```html
* <media-menu>
*   <media-menu-button aria-label="Settings">
*     <media-icon type="settings"></media-icon>
*   </media-menu-button>
*   <media-menu-items>
*     <!-- ... -->
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaMenuButtonElement = class extends Host(HTMLElement, MenuButton) {
	static tagName = "media-menu-button";
};
//#endregion
//#region src/elements/define/menus/menu-item-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/menu}
* @example
* ```html
* <media-menu>
*   <media-menu-items>
*      <media-menu-item></media-menu-item>
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaMenuItemElement = class extends Host(HTMLElement, MenuItem) {
	static tagName = "media-menu-item";
};
//#endregion
//#region src/elements/define/menus/menu-items-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/menu}
* @example
* ```html
* <media-menu>
*   <media-menu-button aria-label="Settings">
*     <media-icon type="settings"></media-icon>
*   </media-menu-button>
*   <media-menu-items>
*     <!-- ... -->
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaMenuItemsElement = class extends Host(HTMLElement, MenuItems) {
	static tagName = "media-menu-items";
};
//#endregion
//#region src/elements/define/menus/speed-radio-group-element.ts
/**
* @part label - Contains the speed option label.
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/speed-radio-group}
* @example
* ```html
* <media-menu>
*   <!-- ... -->
*   <media-menu-items>
*     <media-speed-radio-group>
*       <template>
*         <media-radio>
*           <span data-part="label"></span>
*         </media-radio>
*       </template>
*     </media-speed-radio-group>
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaSpeedRadioGroupElement = class extends Host(HTMLElement, SpeedRadioGroup) {
	static tagName = "media-speed-radio-group";
	onConnect() {
		renderMenuItemsTemplate(this);
	}
};
//#endregion
//#region src/elements/define/menus/quality-radio-group-element.ts
/**
* @part label - Contains the quality option label.
* @part bitrate - Contains the quality option bitrate.
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/quality-radio-group}
* @example
* ```html
* <media-menu>
*   <!-- ... -->
*   <media-menu-items>
*     <media-quality-radio-group>
*       <template>
*         <media-radio>
*           <span data-part="label"></span>
*           <span data-part="bitrate"></span>
*         </media-radio>
*       </template>
*     </media-quality-radio-group>
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaQualityRadioGroupElement = class extends Host(HTMLElement, QualityRadioGroup) {
	static tagName = "media-quality-radio-group";
	onConnect() {
		renderMenuItemsTemplate(this, (el, option) => {
			const bitrate = option.bitrate, bitrateEl = el.querySelector("[data-part=\"bitrate\"]");
			if (bitrate && bitrateEl) effect(() => {
				bitrateEl.textContent = bitrate() || "";
			});
		});
	}
};
//#endregion
//#region src/elements/define/menus/radio-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/radio}
* @example
* ```html
* <media-radio-group value="720">
*   <media-radio value="1080">1080p</media-radio>
*   <media-radio value="720">720p</media-radio>
*   <!-- ... -->
* </media-radio-group>
* ```
*/
var MediaRadioElement = class extends Host(HTMLElement, Radio) {
	static tagName = "media-radio";
};
//#endregion
//#region src/elements/define/sliders/slider-thumbnail-element.ts
/**
* @example
* ```html
* <media-player >
*   <media-time-slider>
*     <media-slider-preview>
*       <media-slider-thumbnail
*         src="https://files.vidstack.io/thumbnails.vtt"
*       ></media-slider-thumbnail>
*     </media-slider-preview>
*   </media-time-slider>
* </media-player>
* ```
*/
var MediaSliderThumbnailElement = class extends MediaThumbnailElement {
	static tagName = "media-slider-thumbnail";
	#media;
	#slider;
	onSetup() {
		super.onSetup();
		this.#media = useMediaContext();
		this.#slider = useState(Slider.state);
	}
	onConnect() {
		super.onConnect();
		effect(this.#watchTime.bind(this));
	}
	#watchTime() {
		const { duration, clipStartTime } = this.#media.$state;
		this.time = clipStartTime() + this.#slider.pointerRate() * duration();
	}
};
//#endregion
//#region src/elements/define/sliders/slider-value-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/slider-value}
* @example
* ```html
* <media-time-slider>
*   <media-slider-preview>
*     <media-slider-value></media-slider-value>
*   </media-slider-preview>
* </media-time-slider>
* ```
* @example
* ```html
* <media-slider-value type="current"></media-slider-value>
* ```
* @example
* ```html
* <media-slider-value show-hours pad-hours></media-slider-value>
* ```
* @example
* ```html
* <media-slider-value decimal-places="2"></media-slider-value>
* ```
*/
var MediaSliderValueElement = class extends Host(HTMLElement, SliderValue) {
	static tagName = "media-slider-value";
	static attrs = { padMinutes: { converter: BOOLEAN } };
	onConnect() {
		effect(() => {
			this.textContent = this.getValueText();
		});
	}
};
//#endregion
//#region src/elements/define/sliders/time-slider-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/time-slider}
* @example
* ```html
* <media-time-slider>
*   <div class="track"></div>
*   <div class="track-fill"></div>
*   <div class="track-progress"></div>
*   <div class="thumb"></div>
* </media-time-slider>
* ```
* @example
* ```html
* <media-time-slider>
*   <!-- ... -->
*   <media-slider-preview>
*     <media-slider-value></media-slider-value>
*   <media-slider-preview>
* </media-time-slider>
* ```
*/
var MediaTimeSliderElement = class extends Host(HTMLElement, TimeSlider) {
	static tagName = "media-time-slider";
};
//#endregion
//#region src/elements/define/sliders/slider-preview-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/slider#preview}
*/
var MediaSliderPreviewElement = class extends Host(HTMLElement, SliderPreview) {
	static tagName = "media-slider-preview";
};
//#endregion
//#region src/elements/define/sliders/volume-slider-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/volume-slider}
* @example
* ```html
* <media-volume-slider>
*   <div class="track"></div>
*   <div class="track-fill"></div>
*   <div class="track-progress"></div>
*   <div class="thumb"></div>
* </media-volume-slider>
* ```
* @example
* ```html
* <media-volume-slider>
*   <!-- ... -->
*   <media-slider-preview>
*     <media-slider-value></media-slider-value>
*   </media-slider-preview>
* </media-volume-slider>
* ```
*/
var MediaVolumeSliderElement = class extends Host(HTMLElement, VolumeSlider) {
	static tagName = "media-volume-slider";
};
//#endregion
export { MediaAirPlayButtonElement, MediaAudioRadioGroupElement, MediaCaptionButtonElement, MediaCaptionsRadioGroupElement, MediaFullscreenButtonElement, MediaLiveButtonElement, MediaMenuButtonElement, MediaMenuElement, MediaMenuItemElement, MediaMenuItemsElement, MediaMuteButtonElement, MediaPIPButtonElement, MediaPlayButtonElement, MediaQualityRadioGroupElement, MediaRadioElement, MediaSeekButtonElement, MediaSliderPreviewElement, MediaSliderThumbnailElement, MediaSliderValueElement, MediaSpeedRadioGroupElement, MediaThumbnailElement, MediaTimeElement, MediaTimeSliderElement, MediaVolumeSliderElement, renderMenuItemsTemplate };
